import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Calendar,
  Users,
  Clock,
  MessageSquare,
  DollarSign,
  Shield,
  Building2,
  FileText,
  HelpCircle,
  Send,
  CheckCircle2,
  AlertTriangle,
  Download,
  Globe,
  GripVertical,
  UserPlus,
  RefreshCw,
} from 'lucide-react';

const LAST_UPDATED = 'May 27, 2026';

const Help = () => {
  const { user, isAdmin, isManager, isAssistantManager } = useAuth();

  const userRole = user?.role || 'employee';
  const isAdminRole = isAdmin && isAdmin();
  const isManagerRole = isManager && isManager();
  const isAssistantManagerRole = isAssistantManager && isAssistantManager();

  // Admins can preview the guide from other roles' perspective
  const [previewRole, setPreviewRole] = useState(
    isAdminRole ? 'admin' :
    isManagerRole ? 'manager' :
    isAssistantManagerRole ? 'assistant_manager' :
    'employee'
  );
  const role = isAdminRole ? previewRole : userRole;

  const helpSections = {
    admin: [
      {
        title: 'User Management',
        icon: Shield,
        content: [
          { step: 'Navigate to "Users" in the sidebar', action: 'Click on the Users menu item' },
          { step: 'Create new users', action: 'Click "Add User" and fill in name, email, password, and role' },
          { step: 'Promote existing employee to Manager/Asst Manager', action: 'Choose "Existing Employee" path in the creation modal to preserve employee history' },
          { step: 'Assign locations to managers', action: 'Select "Managed Locations" when creating/editing manager users' },
          { step: 'Edit user roles', action: 'Click on a user row, then edit their role in the modal' },
          { step: 'Delete users', action: 'Click the trash icon next to a user (cannot delete yourself)' },
        ],
      },
      {
        title: 'Location Management',
        icon: Building2,
        content: [
          { step: 'Navigate to "Locations"', action: 'Click on Locations in the sidebar' },
          { step: 'Create new locations', action: 'Click "Add Location" and enter name, address, and phone' },
          { step: 'Set location timezone', action: 'Choose the correct US timezone when creating or editing a location. Clock-in/out times are recorded relative to this timezone.' },
          { step: 'Edit locations', action: 'Click on a location row to edit details' },
        ],
      },
      {
        title: 'Station / Position Management',
        icon: Building2,
        content: [
          { step: 'Open the Stations panel', action: 'Go to Schedule page and click the "Stations" button' },
          { step: 'Add a station', action: 'Type a name and click "Add". Stations appear in shift creation dropdowns.' },
          { step: 'Edit or delete a station', action: 'Use the edit/trash icons. Deleting deactivates but preserves history.' },
          { step: 'Managers also have access', action: 'Managers can add/edit/delete stations for their assigned locations.' },
        ],
      },
      {
        title: 'Employee Transfers',
        icon: Users,
        content: [
          { step: 'Navigate to "Employees"', action: 'Click on Employees in the sidebar' },
          { step: 'Go to "Transfers" tab', action: 'Click the "Transfers" tab at the top of the page' },
          { step: 'Review pending transfers', action: 'See all transfer requests from managers' },
          { step: 'Approve/Reject transfers', action: 'Click "Approve" or "Reject" on each transfer request' },
        ],
      },
      {
        title: 'Audit Log',
        icon: FileText,
        content: [
          { step: 'Navigate to "Audit Log"', action: 'Click on Audit Log in the sidebar' },
          { step: 'View system activity', action: 'See all actions taken by users across the system' },
          { step: 'Filter by date/action', action: 'Use filters to find specific activities' },
        ],
      },
      {
        title: 'Payroll Management',
        icon: DollarSign,
        content: [
          { step: 'Navigate to "Payroll"', action: 'Click on Payroll in the sidebar' },
          { step: 'Select date range', action: 'Choose Daily, Weekly, Monthly, Annual, or Custom range' },
          { step: 'Use pay period presets', action: 'Click "1st-15th" or "16th-End" for common pay periods' },
          { step: 'View overtime breakdown', action: 'Overtime is calculated per Mon-Sun work week. First 40 hours are regular (1.0x), hours above 40 are overtime (1.5x).' },
          { step: 'Split-week calculation', action: 'If a work week is split across two pay periods, the system calculates the full week total first, subtracts hours already paid in the prior period, and allocates remaining regular/OT to the current period.' },
          { step: 'Export payroll data', action: 'Click "Export" dropdown and choose CSV or PDF. CSV includes detailed pay breakdown. PDF generates a professional printable report.' },
          { step: 'Cost multiplier (Accounting Admin)', action: 'Accounting Admins can adjust the cost multiplier for taxes and benefits overhead.' },
        ],
      },
    ],
    manager: [
      {
        title: 'Schedule Management',
        icon: Calendar,
        content: [
          { step: 'Navigate to "Schedule"', action: 'Click on Schedule in the sidebar. Default view is Monday-Sunday.' },
          { step: 'Add a shift', action: 'Click "+" on any day, select employee, times, station, and notes' },
          { step: 'Drag and drop shifts', action: 'Drag a shift card to a different day or employee row to move it' },
          { step: 'Copy shifts/weeks', action: 'Use "Copy Week" to duplicate entire week schedules. Press Esc or click X to exit copy mode.' },
          { step: 'Publish schedule', action: 'Click "Publish Schedule" to make shifts visible to employees' },
          { step: 'Create open shifts', action: 'Select "Open Shift" as the employee. Employees can view and claim these.' },
          { step: 'Review open shift claims', action: 'The "Pending Claims" panel shows employees who requested an open shift. Approve or reject each claim.' },
        ],
      },
      {
        title: 'Employee Management',
        icon: Users,
        content: [
          { step: 'Navigate to "Employees"', action: 'Click on Employees in the sidebar' },
          { step: 'Add employees', action: 'Click "Add Employee" and fill in details including hire date' },
          { step: 'Edit employee info', action: 'Click "View" on any employee to edit their details' },
          { step: 'Terminate employee', action: 'Click actions menu, then Terminate; enter reason and date' },
          { step: 'Reinstate employee', action: 'In "Former" tab, click Reinstate and enter reason' },
          { step: 'Request transfer', action: 'Click actions menu, then Transfer; select destination location' },
        ],
      },
      {
        title: 'Time Entries',
        icon: Clock,
        content: [
          { step: 'Navigate to "Time Entries"', action: 'Click on Time Entries in the sidebar' },
          { step: 'View clock-in/out history', action: 'See all time entries for your location with day/week/custom range views' },
          { step: 'Late Clock In column', action: 'Shows how many minutes an employee clocked in late compared to their scheduled shift start time. "On Time" means they arrived on or before schedule. "No Schedule" means no shift was scheduled that day.' },
          { step: 'Status column meanings', action: '"Completed" = clocked in and out. "Active" = currently working. "Missed Clock-Out" = clocked in on a past day but never clocked out. "Manual" = entry was added manually by a manager. "Pending" = entry is incomplete.' },
          { step: 'Edit entries', action: 'Click the edit icon on an entry to adjust times' },
          { step: 'Add manual entry', action: 'Click "Add Time Entry" for missed clock-ins. Only Admin and Manager can create manual entries.' },
        ],
      },
      {
        title: 'Team Messaging',
        icon: MessageSquare,
        content: [
          { step: 'Navigate to "Messages"', action: 'Click on Messages in the sidebar' },
          { step: 'Send to employee', action: 'Click "New Message", select employee, type message' },
          { step: 'Send to location group', action: 'Select "All [Location]" in the recipient dropdown' },
          { step: 'Mark as urgent', action: 'Toggle "Urgent" when composing to highlight message' },
        ],
      },
      {
        title: 'Time-Off Requests',
        icon: Calendar,
        content: [
          { step: 'Navigate to "Time Off"', action: 'Click on Time Off in the sidebar' },
          { step: 'Review requests', action: 'See pending time-off requests from employees' },
          { step: 'Approve/Deny', action: 'Click "Approve" or "Deny" on each request' },
        ],
      },
      {
        title: 'Profile & Settings',
        icon: Globe,
        content: [
          { step: 'Open Profile', action: 'Click your name in the lower-left corner, then select "Profile"' },
          { step: 'Set your timezone', action: 'Choose the correct US timezone. This affects how clock-in/out times are displayed.' },
          { step: 'Change password', action: 'Select "Change Password" from the same menu.' },
          { step: 'Auto sign-out', action: 'Managers are automatically signed out after 30 minutes of inactivity for security. A 2-minute warning appears first.' },
        ],
      },
    ],
    assistant_manager: [
      {
        title: 'Viewing Schedules',
        icon: Calendar,
        content: [
          { step: 'Navigate to "Schedule"', action: 'Click on Schedule in the sidebar' },
          { step: 'View shift assignments', action: 'See all scheduled shifts for your location (Mon-Sun view)' },
          { step: 'Navigate weeks', action: 'Use arrows to move between weeks' },
        ],
      },
      {
        title: 'Time Entries',
        icon: Clock,
        content: [
          { step: 'Navigate to "Time Entries"', action: 'Click on Time Entries in the sidebar' },
          { step: 'View employee hours', action: 'See clock-in/out records for your location' },
          { step: 'Late Clock In column', action: 'Shows how many minutes late compared to scheduled start. "On Time" = arrived on schedule.' },
          { step: 'Status column', action: '"Completed" = done. "Active" = still working. "Missed Clock-Out" = no clock-out recorded on a past day.' },
        ],
      },
      {
        title: 'Messaging',
        icon: MessageSquare,
        content: [
          { step: 'Navigate to "Messages"', action: 'Click on Messages in the sidebar' },
          { step: 'Send messages', action: 'Compose and send messages to team members' },
          { step: 'View inbox', action: 'Read and reply to messages' },
        ],
      },
    ],
    employee: [
      {
        title: 'Viewing Your Schedule',
        icon: Calendar,
        content: [
          { step: 'Go to the Schedule tab', action: 'Click "Schedule" on the employee portal' },
          { step: 'View your shifts', action: 'See your upcoming scheduled shifts with times and station' },
          { step: 'Navigate weeks', action: 'Use arrows to see past or future schedules' },
          { step: 'Claim open shifts', action: 'If there are open shifts at your location, click "Claim" to request one. Your manager will approve or deny it.' },
        ],
      },
      {
        title: 'Clocking In/Out',
        icon: Clock,
        content: [
          { step: 'Go to the clock-in kiosk', action: 'Open the kiosk page from the Employee Sign In link' },
          { step: 'Enter your ID', action: 'Type your employee number and passcode' },
          { step: 'Clock in', action: 'Click "Clock In" at the start of your shift. Your time is recorded in your location\'s timezone.' },
          { step: 'Take breaks', action: 'Click "Start Break" and "End Break" as needed' },
          { step: 'Clock out', action: 'Click "Clock Out" at the end of your shift' },
        ],
      },
      {
        title: 'Messaging',
        icon: MessageSquare,
        content: [
          { step: 'Go to the Messages tab', action: 'Click "Messages" on the employee portal' },
          { step: 'View messages', action: 'Read messages from managers' },
          { step: 'Reply to messages', action: 'Type in the reply box below any message and hit send' },
          { step: 'Mark all as read', action: 'Click "Mark All Read" to clear unread message indicators' },
        ],
      },
      {
        title: 'Time-Off Requests',
        icon: Calendar,
        content: [
          { step: 'Go to the Time Off tab', action: 'Click "Time Off" on the employee portal' },
          { step: 'Request time off', action: 'Click "Request Time Off" button' },
          { step: 'Fill in details', action: 'Select dates, type (vacation/sick/personal), and reason' },
          { step: 'Submit request', action: 'Click "Submit Request" and wait for manager approval' },
          { step: 'Check status', action: 'View your requests and their approval status' },
        ],
      },
      {
        title: 'Notifications',
        icon: Send,
        content: [
          { step: 'Check the bell icon', action: 'Click the notification bell in the header' },
          { step: 'View alerts', action: 'See schedule changes, messages, and time-off updates' },
          { step: 'Mark as read', action: 'Click on a notification to mark it as read' },
        ],
      },
    ],
  };

  const whatsNewByRole = {
    admin: [
      { step: 'Delete Time Entries', action: 'Open any time entry → click "Delete Time Entry" inside the edit modal. Confirmation required. Deletion removes the entry from payroll, reports, exports, dashboard totals — and is logged in the Audit Log with who, when, employee, and original clock times.' },
      { step: 'Blackouts & Limits for Managers', action: 'Managers can now manage blackouts and daily off-limits for their assigned locations. Admin retains global control. Track this in the Audit Log → Code Updates tab.' },
      { step: 'Daily Schedule Notes', action: 'On the Schedule page, click the yellow "Day Notes" cell under any day to add or edit notes for that date (deliveries, events, reminders). Employees see notes for their scheduled days inline in My Schedule.' },
      { step: 'Message Attachments', action: 'When composing a new message, click "Attach file or picture". Images, PDFs, docs up to 10 MB are supported. Files are stored in Emergent object storage; downloads stream through /api/uploads/{id} with role-based auth.' },
      { step: 'Daily Schedule Summary', action: 'A purple footer row on the Schedule shows per-day people count, total scheduled hours, and estimated wages. Excludes the Location Manager; includes Assistant Managers and regular employees.' },
      { step: 'Coding log location', action: 'Audit Log → Code Updates tab now stores Update Type, Summary, Reason, Areas Affected, Files/Modules, Testing Completed, and Known Issues for every release.' },
    ],
    manager: [
      { step: 'Delete Time Entries', action: 'Edit any time entry for your assigned location and use "Delete Time Entry". Confirms before deleting. Entry is removed from payroll, totals, and exports; deletion logged.' },
      { step: 'Manage Blackouts & Limits', action: 'On the Time Off tab, click "Blackouts & Limits". You can now create blackout dates and per-day off-limits for your assigned location(s). Use the location selector to pick which assigned location to manage.' },
      { step: 'Daily Schedule Notes', action: 'Schedule page → click the yellow "Day Notes" cell under any day. Notes apply only to your assigned location and appear automatically on scheduled employees\' portal view.' },
      { step: 'Message Attachments', action: 'Send messages with images or files attached. Click "Attach file or picture" in the New Message dialog. Recipients can open or download.' },
      { step: 'Daily Schedule Summary', action: 'A purple summary row shows total scheduled people, hours, and estimated wages for each day. Helps with quick labor-cost checks while scheduling.' },
    ],
    assistant_manager: [
      { step: 'View Blackouts & Limits', action: 'You can now view (and manage where allowed) blackouts and daily off-limits for your assigned locations under Time Off → Blackouts & Limits.' },
      { step: 'Daily Schedule Notes', action: 'View daily notes on the Schedule page. Editing access follows your existing schedule permissions.' },
      { step: 'Message Attachments', action: 'Send and receive messages with attached files or images.' },
      { step: 'Daily Schedule Summary', action: 'See per-day people scheduled, hours, and estimated wages (excludes the Location Manager).' },
    ],
    employee: [
      { step: 'Blackout dates on time-off calendar', action: 'When requesting time off, blackout dates now appear shaded with a strikethrough in the calendar — they cannot be selected. A red banner at the top of the modal lists upcoming blackouts.' },
      { step: 'Schedule notes from your manager', action: 'In My Schedule, look for a yellow "Notes" pill under each day. It shows day-specific reminders (deliveries, special events, etc.) from your manager.' },
      { step: 'Attachments in messages', action: 'Messages can include image previews and downloadable files. Click an image to enlarge or a file pill to download.' },
    ],
  };

  const buildWhatsNewSection = () => ({
    title: 'What\'s New (February 2026)',
    icon: HelpCircle,
    content: whatsNewByRole[role] || whatsNewByRole.employee,
  });

  const getSectionsForRole = () => {
    const whatsNew = buildWhatsNewSection();
    if (role === 'admin' || role === 'accounting_admin') return [whatsNew, ...helpSections.admin, ...helpSections.manager];
    if (role === 'manager') return [whatsNew, ...helpSections.manager];
    if (role === 'assistant_manager') return [whatsNew, ...helpSections.assistant_manager];
    return [whatsNew, ...helpSections.employee];
  };

  const sections = getSectionsForRole();

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <HelpCircle className="w-8 h-8 text-purple-600" />
          Help & Tutorials
        </h1>
        <p className="text-gray-500 mt-1">
          Step-by-step guides for using STP TeamBase
        </p>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          <Badge className="bg-purple-100 text-purple-700" data-testid="help-current-role-badge">
            Showing guides for: {role.replace('_', ' ').toUpperCase()}
          </Badge>
          <Badge variant="outline" className="text-gray-500" data-testid="help-last-updated">
            Updated as of {LAST_UPDATED}
          </Badge>
          {isAdminRole && (
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-gray-500">Preview as:</span>
              <Select value={previewRole} onValueChange={setPreviewRole}>
                <SelectTrigger className="w-[200px]" data-testid="help-role-preview-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="assistant_manager">Assistant Manager</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      <div className="grid gap-6">
        {sections.map((section, idx) => (
          <Card key={section.title} className="border-0 shadow-md">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-3 text-lg">
                <section.icon className="w-5 h-5 text-purple-600" />
                {section.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {section.content.map((item, stepIdx) => (
                  <AccordionItem key={item.step} value={`item-${idx}-${stepIdx}`}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3 text-left">
                        <div className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 text-purple-600 text-sm font-semibold">
                          {stepIdx + 1}
                        </div>
                        <span>{item.step}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="ml-9 flex items-start gap-2 text-gray-600">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{item.action}</span>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Reference: Column Definitions */}
      <Card className="mt-6 border-0 shadow-md">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-purple-600" />
            Column Definitions (Time Entries)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Late Clock In</h4>
              <p className="text-sm text-gray-600">
                Compares the employee&apos;s actual clock-in time against their scheduled shift start time for that day.
                If they clock in after the scheduled start, it shows how many minutes late (e.g., &quot;15 min late&quot;).
                If they clock in on time or early, it shows &quot;On Time&quot;.
                If no shift is scheduled for that day, it shows &quot;No Schedule&quot;.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Status</h4>
              <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
                <li><span className="font-medium text-green-700">Completed</span> &mdash; Employee clocked in and clocked out normally.</li>
                <li><span className="font-medium text-purple-700">Active</span> &mdash; Employee is currently clocked in and working (today).</li>
                <li><span className="font-medium text-red-700">Missed Clock-Out</span> &mdash; Employee clocked in on a past day but never clocked out. A manager should edit this entry.</li>
                <li><span className="font-medium text-blue-700">Manual</span> &mdash; Entry was manually created by a manager/admin for a missed punch.</li>
                <li><span className="font-medium text-yellow-700">Pending</span> &mdash; Entry is incomplete or awaiting further action.</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Tips */}
      <Card className="mt-6 border-0 shadow-md bg-gradient-to-r from-purple-50 to-blue-50">
        <CardHeader>
          <CardTitle className="text-lg">Quick Tips</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-gray-600">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-purple-500 mt-1 flex-shrink-0" />
              <span>Click the notification bell to see real-time updates</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-purple-500 mt-1 flex-shrink-0" />
              <span>Use the location dropdown (top-left) to switch between locations</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-purple-500 mt-1 flex-shrink-0" />
              <span>Schedules must be published before employees can see them</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-purple-500 mt-1 flex-shrink-0" />
              <span>Managers can change their password and set timezone from the Profile menu (lower-left corner)</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-purple-500 mt-1 flex-shrink-0" />
              <span>Use &quot;Export&quot; on the Payroll page to download CSV or PDF reports for accounting</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-purple-500 mt-1 flex-shrink-0" />
              <span>Drag and drop shifts on the Schedule page to quickly reschedule</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-purple-500 mt-1 flex-shrink-0" />
              <span>Each page has a refresh button to pull the latest data without reloading</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default Help;
